// client/src/context/AuthProvider.jsx

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from './AuthContext';
import { setAccessToken, clearAccessToken } from '../services/api';
import * as authService from '../services/auth.service';
import Spinner from '../components/layout/Spinner';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try {
      await authService.logoutUser();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      clearAccessToken();
      queryClient.setQueryData(['session'], null);
    }
  }, [queryClient]);

  // Devolvemos la lógica de verificación a un useEffect, un patrón estable y probado.

  useEffect(() => {
    const verifySession = async () => {
      try {
        const { data } = await authService.verifyCurrentSession();
        if (data && data.user && data.accessToken) {
          setUser(data.user);
          setAccessToken(data.accessToken);
          queryClient.setQueryData(['session'], data);
        } else {
          setUser(null);
          clearAccessToken();
        }
      } catch (error) {
        console.log('No active session found or session is invalid.') + error;
        setUser(null);
        clearAccessToken();
      } finally {
        setIsLoading(false);
      }
    };

    verifySession();
  }, [queryClient]);

  // Auto-refresh cada 15 minutos (solo si está autenticado)
  useEffect(() => {
    if (!user) return;

    const autoRefresh = async () => {
      try {
        const { data } = await authService.verifyCurrentSession();
        if (data?.accessToken) {
          setAccessToken(data.accessToken);
        }
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    };

    const interval = setInterval(autoRefresh, 15 * 60 * 1000); // 15 minutos

    return () => clearInterval(interval);
  }, [user]);
  useEffect(() => {
    const handleAuthError = () => {
      logout();
    };

    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, [logout]);

  const login = useCallback(
    async (credentials) => {
      const { accessToken, user } = await authService.loginUser(credentials);
      setAccessToken(accessToken);
      setUser(user);
      queryClient.setQueryData(['session'], { accessToken, user });
      return user;
    },
    [queryClient]
  );

  const register = useCallback(
    async (userData) => {
      const { accessToken, user, message } = await authService.registerUser(
        userData
      );
      setAccessToken(accessToken);
      setUser(user);
      queryClient.setQueryData(['session'], { accessToken, user });
      return { message };
    },
    [queryClient]
  );

  const updateUserState = useCallback((updatedUserData) => {
    setUser((currentUser) =>
      currentUser ? { ...currentUser, ...updatedUserData } : null
    );
  }, []);

  const refreshUserData = useCallback(async () => {
    try {
      const { data } = await authService.verifyCurrentSession();
      setUser(data.user);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      logout();
    }
  }, [logout]);

  const checkUsernameExists = useCallback(async (username) => {
    return await authService.checkAvailability('username', username);
  }, []);

  const checkEmailExists = useCallback(async (email) => {
    return await authService.checkAvailability('email', email);
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      register,
      updateUserState,
      refreshUserData,
      checkUsernameExists,
      checkEmailExists,
    }),
    [
      user,
      isLoading,
      login,
      logout,
      register,
      updateUserState,
      refreshUserData,
      checkUsernameExists,
      checkEmailExists,
    ]
  );

  // El AuthProvider vuelve a mostrar un Spinner mientras verifica la sesión.
  if (isLoading) {
    return <Spinner fullScreen={true} />;
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
